import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { translations, type Language, LANGUAGES } from './translations';

interface LanguageContextValue {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
    languages: typeof LANGUAGES;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function getInitialLanguage(): Language {
    if (typeof window === 'undefined') return 'en';
    const stored = localStorage.getItem('podus-lang');
    if (stored && stored in translations) return stored as Language;
    return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [language, setLang] = useState<Language>(getInitialLanguage);

    const setLanguage = useCallback((lang: Language) => {
        setLang(lang);
        localStorage.setItem('podus-lang', lang);
    }, []);

    const t = useCallback(
        (key: string): string => {
            return translations[language][key] ?? translations.en[key] ?? key;
        },
        [language],
    );

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t, languages: LANGUAGES }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
    return ctx;
}
